import React, { useCallback, useContext, useEffect, useState } from "react";
import { format, getYear } from "date-fns";

import { COLOR_VALUES } from "shared/config/colors";
import DropdownSingleSelectWithoutSearch from "shared/components/inputs/dropdown/DropdownSingleSelectWithoutSearch";
import FormInputWrapper from "shared/components/inputs/form/FormInputWrapper";
import HStack from "shared/components/layout/stack/HStack";
import Loading from "shared/components/feedback/loading/Loading";
import SecondaryButton from "shared/components/inputs/button/SecondaryButton";
import SessionContext from "app/contexts/SessionContext";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";
import useTimeOffApi from "../querys/useTimeOff";
import { LoadingWrapper, ResponsiveButtonWrapper } from "./MyRequestTimeOffTable.styles";

const FiltersConfig = [
    {
        name: "status",
        columnAccessor: "status",
        unfilteredValue: "All",
        isSegmentedControl: true,
        options: [
            {
                value: "All",
                label: "All",
            },
            {
                value: "Pending",
                label: "Pending",
            },
            {
                value: "Approved",
                label: "Approved",
            },
            {
                value: "Rejected",
                label: "Rejected",
            },
        ],
    },
];

const getHoverMessageTooltip = (value) => {
    let tooltipHoverMessage = "";
    const today = new Date();
    const isPastFirstDate = new Date(value?.firstDayOut) < today;
    const isPastEndDate = new Date(value?.lastDayOut) <= today;

    if (
        value.rejected ||
        (value.approved && isPastFirstDate) ||
        (value.pending && isPastEndDate)
    ) {
        tooltipHoverMessage = "View Request";
    } else if (
        (value.approved && !isPastFirstDate) ||
        (value.pending && !isPastEndDate)
    ) {
        tooltipHoverMessage = "View or Edit Request";
    }
    return tooltipHoverMessage;
};

const getDurationTootip = (value) => {
    let tooltipFirstDay = "";
    if (value.firstDayOutMorning && !value.firstDayOutAfternoon) {
        tooltipFirstDay = "Morning Out";
    } else if (value.firstDayOutAfternoon && !value.firstDayOutMorning) {
        tooltipFirstDay = "Afternoon Out";
    }

    const isOneDay = value.firstDayOut === value.lastDayOut;
    const startDatePrefix = isOneDay || tooltipFirstDay === ""
        ? ""
        : "Start Date: ";

    let tooltipLastDay = "";
    if (!isOneDay) {
        if (value.lastDayOutMorning && !value.lastDayOutAfternoon) {
            tooltipLastDay = "End Date: Morning Out";
        } else if (value.lastDayOutAfternoon && !value.lastDayOutMorning) {
            tooltipLastDay = "End Date: Afternoon Out";
        }
    }
    const lineBreak = tooltipFirstDay && tooltipLastDay ? "\n" : "";
    return `${startDatePrefix}${tooltipFirstDay}${lineBreak}${tooltipLastDay}`;
};

const getIcon = (value) => {
    if (value === "Approved") {
        return "success";
    } else if (value === "Rejected") {
        return "error";
    } else return null;
};

const getStatusLabel = (value) => {
    if (value.pending && value.deparmentApproved) {
        return "Pending HR Approval";
    }
    return value.status;
};

const getIconColor = (value) => {
    if (value === "Approved") {
        return "Green";
    } else if (value === "Rejected") {
        return "Orange_1";
    } else return null;
};

const getRowProps = (row) => ({
    style: {
        background: row.values.Active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const getYears = (earliestYear) => {
    let years = [{ value: "All", label: "All" }];
    let currentYear = new Date().getFullYear() + 1;
    earliestYear = earliestYear ?? 1978;
    while (currentYear >= earliestYear) {
        years.push({ value: currentYear, label: currentYear });
        currentYear -= 1;
    }
    return years;
};

const tableCTAProps = {
    leadingIcon: "expand",
    label: "Request Time Off",
    to: "?action=add",
    replace: true,
};

const currentYear = getYear(new Date());

const TimeOffTable = () => {
    const isSmallScreen = useSmallBreakpoint();

    const session = useContext(SessionContext);
    const userStarDate = new Date(session.startDate);
    const yearsToShow = getYears(getYear(userStarDate));

    const { useGetTimeOffRequests } = useTimeOffApi();
    const timeOffRequestsQuery = useGetTimeOffRequests({ employeeId: session.legacyId });

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [filters, setFilters] = useState();
    const [filtersAll, setFiltersAll] = useState([
        {
            id: "firstDayOut",
            value: currentYear,
        },
    ]);

    // Configuración de columnas que depende del tamaño de pantalla
    const columns = [
        {
            headCellProps: {
                text: isSmallScreen ? "Start" : "Start Date",
                width: isSmallScreen ? "23%" : "15%",
            },
            getBodyCellProps: (cell) => ({
                text: isSmallScreen
                    ? format(new Date(cell.value), "MMM dd, yyyy")
                    : format(new Date(cell.value), "MMM dd, yyyy") ?? "-",
                ellipsisAtLine: isSmallScreen ? 2 : null,
                width: isSmallScreen ? "23%" : "15%",
            }),
            filter: (rows, _columnIds, filterValue) => {
                if (filterValue === "All") {
                    return rows;
                } else {
                    return rows.filter((row) => {
                        return (
                            filterValue ===
                            new Date(row.values.firstDayOut).getFullYear()
                        );
                    });
                }
            },
            accessor: "firstDayOut",
            show: true,
        },
        {
            headCellProps: {
                text: isSmallScreen ? "End" : "End Date",
                width: isSmallScreen ? "23%" : "15%",
            },
            getBodyCellProps: (cell) => ({
                text: format(new Date(cell.value), "MMM dd, yyyy") ?? "-",
                ellipsisAtLine: isSmallScreen ? 2 : null,
                width: isSmallScreen ? "23%" : "15%",
            }),
            accessor: "lastDayOut",
            show: true,
        },
        {
            headCellProps: {
                text: "Duration",
                width: isSmallScreen ? "12%" : "15%",
            },
            getBodyCellProps: (cell) => ({
                text: cell.value,
                tooltip: getDurationTootip(cell.row.original),
                tooltipEnterDelay: 0,
                placement: "bottom-start",
                ellipsisAtLine: isSmallScreen ? 2 : null,
            }),
            accessor: "amountOfDaysOut",
            show: !isSmallScreen,
        },
        {
            headCellProps: {
                text: "Reason",
                width: isSmallScreen ? "27%" : "15%",
            },
            getBodyCellProps: (cell) => ({
                text: cell.value ?? "-",
                ellipsisAtLine: isSmallScreen ? 2 : null,
                width: isSmallScreen ? "27%" : "15%",
            }),
            accessor: "reasonName",
            show: true,
        },
        {
            headCellProps: {
                text: "Submitted",
                width: "18%",
            },
            getBodyCellProps: (cell) => ({
                text: format(new Date(cell.value), "MMM dd, yyyy") ?? "-",
                ellipsisAtLine: isSmallScreen ? 2 : null,
                width: "15%",
            }),
            accessor: "createdDate",
            show: !isSmallScreen,
        },
        {
            headCellProps: {
                text: "Status",
                width: isSmallScreen ? "20%" : "18%",
            },
            getBodyCellProps: (cell) => {
                const icon = getIcon(cell.value);
                const hasIcon = icon !== null;
                
                return {
                    // Si tiene ícono en móvil, no mostrar texto
                    text: (isSmallScreen && hasIcon) ? null : getStatusLabel(cell.row.original),
                    icon: icon,
                    iconColor: getIconColor(cell.value),
                    ellipsisAtLine: isSmallScreen ? 2 : null,
                    tooltip: isSmallScreen && hasIcon ? getStatusLabel(cell.row.original) : undefined,
                    tooltipEnterDelay: 0,
                    justifyContent: (isSmallScreen && hasIcon) ? "center" : "start",
                };
            },
            accessor: "status",
            filter: (rows, _columnIds, filterValue) => {
                if (filterValue === "All") {
                    return rows;
                } else {
                    return rows.filter((row) => {
                        return filterValue === row.values.status;
                    });
                }
            },
            show: true,
        },
        {
            headCellProps: {
                width: isSmallScreen ? "7%" : "9%",
            },
            accessor: "edit",
            disableSortBy: true,
            getBodyCellProps: (cell) => ({
                shownByRowHover: !isSmallScreen,
                actionIcon: "file",
                tooltipEnterDelay: 0,
                tooltip: getHoverMessageTooltip(cell.row.original),
                actionProps: {
                    to: `?action=edit&id=${cell.row.original.id}`,
                    replace: true,
                    "aria-label": getHoverMessageTooltip(cell.row.original),
                },
            }),
            show: true,
        },
    ];

    const handleOnChangeDropdown = (val, type) => {
        setSelectedYear(val);
        let filtersNew = [...filtersAll];
        if (filtersNew?.length > 0) {
            filtersNew = filtersNew.filter((x) => x.id !== type);
        }
        filtersNew.push({
            id: "firstDayOut",
            value: val,
        });
        setFiltersAll(filtersNew);
    };

    const applyFilter = useCallback(() => {
        setFiltersAll(prevFiltersAll => {
            let filtersNew = [...prevFiltersAll];
            if (filtersNew?.length > 0) {
                filtersNew = filtersNew.filter((f) => f.id !== "status");
            }
            if (filters?.length > 0) {
                filtersNew.push(filters?.[0]);
            }
            return filtersNew;
        });
    }, [filters]);

    useEffect(() => {
        applyFilter();
    }, [filters, applyFilter]);


    if (timeOffRequestsQuery.isLoading || timeOffRequestsQuery.isFetching) {
        return (
            <LoadingWrapper>
                <Loading text="Loading requests..." />
            </LoadingWrapper>
        );
    }

    if (timeOffRequestsQuery.isRefetching) {
        return (
            <LoadingWrapper>
                <Loading text="Updating requests..." />
            </LoadingWrapper>
        );
    }

    return (
        <VStack>
            <HStack align="center" justify="space-between" wrap="true">
                    <HStack align="flex-end" wrap="true">
                        <TableFilters
                            filtersConfig={FiltersConfig}
                            onChange={setFilters}
                        />
                        <FormInputWrapper
                            className="max-width-190"
                            label="Year"
                            isSmallSize={true}
                        >
                            <DropdownSingleSelectWithoutSearch
                                isSmallSize={true}
                                options={yearsToShow}
                                value={selectedYear}
                                hideDropDownSearch={true}
                                onChange={(val) =>
                                    handleOnChangeDropdown(
                                        val,
                                        "firstDayOut"
                                    )
                                }
                                position="absolute"
                            />
                        </FormInputWrapper>
                    </HStack>
                    <ResponsiveButtonWrapper $isSmallScreen={isSmallScreen}>
                        <SecondaryButton {...tableCTAProps} fullWidth={isSmallScreen} />
                    </ResponsiveButtonWrapper>
                </HStack>
                {
                    <Table
                        columns={columns}
                        data={timeOffRequestsQuery.data || []}
                        getRowProps={getRowProps}
                        filters={filtersAll}
                        pageSizeInitially={10}
                        expandedAllRows={isSmallScreen}
                    />
                }
        </VStack>
    );
};

export default React.memo(TimeOffTable);